import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { useI18n } from "@/lib/I18nContext";
import { toast } from "sonner";
import { Trophy, Target, TrendingUp, Pencil } from "lucide-react";
import { Link } from "react-router-dom";
import SecuritySection from "@/components/SecuritySection";

export default function MiPerfil() {
  const { user, updateNickname } = useAuth();
  const { t } = useI18n();
  const [pools, setPools] = useState([]);
  const [selectedPool, setSelectedPool] = useState("");
  const [poolRanking, setPoolRanking] = useState([]);
  const [globalRanking, setGlobalRanking] = useState([]);
  const [myPreds, setMyPreds] = useState({ matches: [], tournament: {} });
  const [matches, setMatches] = useState([]);
  const [editing, setEditing] = useState(false);
  const [nick, setNick] = useState(user?.nickname || "");

  useEffect(() => {
    setNick(user?.nickname || "");
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [pp, gr, p, m] = await Promise.all([
          api.get("/pools").catch(() => ({ data: [] })),
          api.get("/ranking"),
          api.get("/predictions/me"),
          api.get("/tournament/matches"),
        ]);
        if (cancelled) return;
        setPools(pp.data || []);
        setGlobalRanking(gr.data);
        setMyPreds(p.data);
        setMatches(m.data);
        // Initialize selected pool to the user's first-registered pool
        if ((pp.data || []).length > 0) {
          setSelectedPool((prev) => prev || pp.data[0].pool_id);
        }
      } catch {
        // Background refresh: stay quiet so we don't disturb the user.
        // If the request really failed for an auth reason, the global
        // axios interceptor handles the redirect to /login.
      }
    };
    load();
    // Refresh when tab becomes visible
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVis);
    // Refresh every 45s
    const interval = setInterval(load, 45000);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVis);
      clearInterval(interval);
    };
  }, []);

  // Fetch ranking for selected pool whenever it changes
  useEffect(() => {
    if (!selectedPool) { setPoolRanking([]); return; }
    let cancelled = false;
    const fetchPoolRanking = async () => {
      try {
        const r = await api.get(`/pools/${selectedPool}/ranking`);
        if (!cancelled) setPoolRanking(r.data || []);
      } catch {
        if (!cancelled) setPoolRanking([]);
      }
    };
    fetchPoolRanking();
    const interval = setInterval(fetchPoolRanking, 45000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [selectedPool]);

  // Position + points: prefer pool row when pool selected, otherwise global
  const myPoolRow = poolRanking.find((r) => r.user_id === user?.user_id);
  const myGlobalRow = globalRanking.find((r) => r.user_id === user?.user_id);
  const myRow = myPoolRow || myGlobalRow;
  const selectedPoolName = pools.find((p) => p.pool_id === selectedPool)?.name;
  const totalMatches = matches.length || 104;
  // Match counts split by phase
  const fase1Total = matches.filter((m) => m.stage === "group").length || 72;
  const fase2Total = matches.filter((m) => m.stage !== "group").length || 32;
  const predFase1 = (myPreds.matches || []).filter((p) => {
    const m = matches.find((x) => x.match_id === p.match_id);
    return m && m.stage === "group" && (p.home_score != null && p.away_score != null);
  }).length;
  const predFase2 = (myPreds.matches || []).filter((p) => {
    const m = matches.find((x) => x.match_id === p.match_id);
    return m && m.stage !== "group" && ((p.home_score != null && p.away_score != null) || p.winner_team);
  }).length;
  const tournamentSet = ["champion", "runner_up", "third"].filter((k) => myPreds.tournament?.[k]).length;

  const saveNick = async () => {
    try {
      await updateNickname(nick.trim());
      toast.success("Apodo actualizado");
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al actualizar");
    }
  };

  return (
    <div className="space-y-8" data-testid="perfil-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-2 font-mono-data">{t("profile.kicker")}</div>
          <h1 className="font-display text-5xl md:text-6xl uppercase leading-none">
            {t("profile.hello")} <span className="text-[var(--c-lime)]" translate="no">{user?.nickname}</span>
          </h1>
          <div className="mt-2 text-zinc-400 text-sm">{user?.email}</div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <button
            onClick={() => setEditing(!editing)}
            data-testid="edit-nickname-button"
            className="border border-zinc-800 px-4 py-2 text-xs uppercase tracking-widest hover:border-white flex items-center gap-2"
          >
            <Pencil size={12} /> {t("profile.editNickname")}
          </button>
          <div className="text-[10px] text-zinc-500 max-w-[220px] text-right leading-snug">
            {t("profile.nicknameHelp")}
          </div>
        </div>
      </div>

      {editing && (
        <div className="bg-zinc-900 border border-zinc-800 p-6 flex gap-3 items-center">
          <input
            value={nick}
            onChange={(e) => setNick(e.target.value)}
            data-testid="nickname-edit-input"
            className="flex-1 bg-black border border-zinc-800 focus:border-[var(--c-lime)] px-3 py-2 outline-none rounded-sm"
            maxLength={20}
          />
          <button onClick={saveNick} data-testid="save-nickname-button" className="bg-[var(--c-lime)] text-black font-bold uppercase tracking-wider px-4 py-2 text-xs">{t("common.save")}</button>
        </div>
      )}

      {/* Stats: 4 cards (Puntos, Posición, Pronósticos Fase 1, Pronósticos Fase 2) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon={<Trophy size={18} />}
          label={t("profile.stats.points")}
          value={myRow?.points ?? 0}
          color="var(--c-yellow)"
          testid="stat-points"
        />
        <StatCard
          icon={<TrendingUp size={18} />}
          label={t("profile.stats.position")}
          value={myRow?.position ? `${t("profile.stats.no")} ${myRow.position}` : `${t("profile.stats.no")} 0`}
          color="var(--c-peach)"
          testid="stat-position"
          subtext={selectedPoolName || undefined}
          extra={
            pools.length > 1 ? (
              <select
                value={selectedPool}
                onChange={(e) => setSelectedPool(e.target.value)}
                data-testid="pool-position-selector"
                className="mt-3 w-full bg-black border border-zinc-800 focus:border-[var(--c-peach)] px-2 py-1.5 text-[11px] uppercase tracking-wider outline-none rounded-sm font-mono-data"
                onClick={(e) => e.preventDefault()}
              >
                {pools.map((p) => (
                  <option key={p.pool_id} value={p.pool_id}>{p.name}</option>
                ))}
              </select>
            ) : null
          }
        />
        <StatCard
          icon={<Target size={18} />}
          label={t("profile.stats.phase1Picks")}
          value={`${predFase1}/${fase1Total}`}
          color="var(--c-lime)"
          testid="stat-fase1"
        />
        <StatCard
          icon={<Target size={18} />}
          label={t("profile.stats.phase2Picks")}
          value={`${predFase2}/${fase2Total}`}
          color="var(--c-mint)"
          testid="stat-fase2"
        />
      </div>

      {/* Row 1: Noticias · Reglamento · Tabla de posiciones */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link
          to="/noticias"
          data-testid="cta-noticias"
          className="block bg-zinc-900 border border-zinc-800 p-6 md:p-8 min-h-[160px] hover:border-white transition-colors"
        >
          <div className="font-display text-3xl md:text-4xl uppercase leading-tight text-[var(--c-peach)]">
            {t("profile.cta.news")}
          </div>
        </Link>
        <Link
          to="/reglas"
          data-testid="cta-reglas"
          className="block bg-[var(--c-yellow)] text-black p-6 md:p-8 min-h-[160px] hover:bg-yellow-300 transition-colors"
        >
          <div className="font-display text-3xl md:text-4xl uppercase leading-tight">{t("profile.cta.rules")}</div>
        </Link>
        <Link
          to="/ranking"
          data-testid="cta-ranking"
          className="block bg-zinc-900 border border-zinc-800 p-6 md:p-8 min-h-[160px] hover:border-white transition-colors"
        >
          <div className="font-display text-3xl md:text-4xl uppercase leading-tight text-[var(--c-yellow)]">
            {t("profile.cta.ranking")}
          </div>
        </Link>
      </div>

      {/* Row 2: Tus Pronósticos Fase 1 · Tus Pronósticos Fase 2 · Marcadores reales y pronósticos del grupo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link
          to="/pronosticos/fase1"
          data-testid="cta-pronosticos"
          className="block bg-[var(--c-lime)] text-black p-6 md:p-8 min-h-[180px] hover:bg-lime-300 transition-colors"
        >
          <div className="font-display text-2xl md:text-3xl uppercase leading-tight">
            {t("profile.cta.phase1")}
          </div>
          <div className="mt-4 text-xs font-bold">{t("profile.cta.phase1.sub")} →</div>
        </Link>
        <Link
          to="/pronosticos/fase2"
          data-testid="cta-fase2"
          className="block bg-zinc-900 border border-zinc-800 p-6 md:p-8 min-h-[180px] hover:border-white transition-colors"
        >
          <div className="font-display text-2xl md:text-3xl uppercase leading-tight text-[var(--c-lime)]">
            {t("profile.cta.phase2")}
          </div>
          <div className="mt-4 text-xs text-zinc-300 font-bold">{t("profile.cta.phase2.sub")} →</div>
        </Link>
        <Link
          to="/todos"
          data-testid="cta-todos"
          className="block bg-[var(--c-peach)] text-black p-6 md:p-8 min-h-[180px] hover:opacity-90 transition-opacity"
        >
          <div className="font-display text-2xl md:text-3xl uppercase leading-tight">
            {t("profile.cta.realScores")}
          </div>
        </Link>
      </div>

      {/* Security section: change password + 2FA (admin only) */}
      <SecuritySection />
    </div>
  );
}

function StatCard({ icon, label, value, subtext, color, testid, extra }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 p-6 card-hover" data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">
        <div style={{ color }}>{icon}</div>
        <div className="text-[11px] uppercase tracking-widest text-white font-semibold font-mono-data">{label}</div>
      </div>
      <div className="font-display text-5xl md:text-6xl uppercase leading-none" style={{ color }}>{value}</div>
      {subtext && <div className="text-xs text-zinc-400 mt-2 font-mono-data uppercase tracking-wider truncate" title={subtext}>{subtext}</div>}
      {extra}
    </div>
  );
}
